import React, { useEffect, useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';

const Hero = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative pt-32 md:pt-36 pb-20 md:pb-28 overflow-hidden">
      <div className="frame">
        {/* Top eyebrow row */}
        <div
          className={`flex items-center gap-3 pb-8 opacity-0-init ${mounted ? 'animate-rise-soft' : ''}`}
        >
          <span className="chip-lime inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" strokeWidth={2} />
            Innovation meets Excellence
          </span>
          <span className="label-muted hidden sm:inline">Application development · since 2014</span>
        </div>

        {/* Main grid: headline + terminal card */}
        <div className="grid grid-cols-12 gap-x-6 lg:gap-x-10 gap-y-12 items-end">
          {/* Headline column */}
          <div className="col-span-12 lg:col-span-8">
            <h1 className="display-xl">
              <span className={`block opacity-0-init ${mounted ? 'animate-rise animation-delay-100' : ''}`}>
                Transforming Ideas
              </span>
              <span className={`block opacity-0-init ${mounted ? 'animate-rise animation-delay-300' : ''}`}>
                Into <span className="hl-lime">Powerful</span>
              </span>
              <span className={`block opacity-0-init ${mounted ? 'animate-rise animation-delay-500' : ''}`}>
                Applications<span className="text-accent">.</span>
              </span>
            </h1>

            <p className={`mt-8 max-w-measure text-lg md:text-xl text-ink-soft leading-relaxed opacity-0-init ${mounted ? 'animate-rise-soft animation-delay-500' : ''}`}>
              We create cutting-edge software solutions that drive business growth,
              enhance user experiences, and revolutionize the way companies operate
              in the digital age.
            </p>

            <div className={`mt-10 flex flex-wrap items-center gap-3 opacity-0-init ${mounted ? 'animate-rise-soft animation-delay-700' : ''}`}>
              <button onClick={() => scrollTo('services')} className="btn-lime">
                <span>Explore Our Services</span>
                <ChevronDown className="w-4 h-4" strokeWidth={2} />
              </button>
              <button onClick={() => scrollTo('contact')} className="btn-ghost">
                <span>Get in Touch</span>
              </button>
            </div>
          </div>

          {/* Terminal-style status card */}
          <div className={`col-span-12 lg:col-span-4 opacity-0-init ${mounted ? 'animate-rise animation-delay-700' : ''}`}>
            <div className="terminal corner-bracket text-ink">
              <div className="terminal-bar">
                <span className="terminal-dot bg-[#FF5F56]"></span>
                <span className="terminal-dot bg-[#FFBD2E]"></span>
                <span className="terminal-dot bg-[#27C93F]"></span>
                <span className="ml-3 font-mono text-[11px] text-muted uppercase tracking-label">~/charliecollc — zsh</span>
              </div>
              <div className="terminal-body space-y-1.5 num">
                <p><span className="text-muted">$</span> studio.status</p>
                <p className="text-muted">› returning system_state…</p>
                <p>
                  <span className="text-accent">studio</span>
                  <span className="text-muted">: </span>
                  <span>online</span>
                  <span className="ml-2 status-dot align-middle" aria-hidden></span>
                </p>
                <p><span className="text-accent">projects</span><span className="text-muted">: </span>500+ delivered</p>
                <p><span className="text-accent">satisfaction</span><span className="text-muted">: </span>99%</p>
                <p><span className="text-accent">support</span><span className="text-muted">: </span>24 / 7</p>
                <p><span className="text-accent">stack</span><span className="text-muted">: </span>[mobile, web, cloud, ai]</p>
                <p><span className="text-accent">contact</span><span className="text-muted">: </span>support@charliecollc.com</p>
                <p className="pt-2">
                  <span className="text-muted">$</span> <span className="hl-lime">_</span><span className="ml-0.5 inline-block w-2 h-4 bg-ink align-middle animate-blink"></span>
                </p>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between label-muted px-1">
              <span>system / studio.status</span>
              <span>OK · 200</span>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className={`mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-rule-soft border border-rule-soft opacity-0-init ${mounted ? 'animate-rise-soft animation-delay-900' : ''}`}>
          {[
            { n: '500+', l: 'Projects Delivered' },
            { n: '99%',  l: 'Client Satisfaction' },
            { n: '24/7', l: 'Support Available' },
          ].map((s) => (
            <div key={s.l} className="bg-paper-2 p-6 md:p-8 text-center md:text-left">
              <div className="font-display font-semibold text-4xl md:text-5xl tracking-tight num">{s.n}</div>
              <div className="mt-3 label">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Scroll indicator */}
        <div className="mt-14 flex justify-center">
          <button
            onClick={() => scrollTo('services')}
            className="group flex flex-col items-center gap-3"
            aria-label="Scroll to services"
          >
            <span className="label-muted group-hover:text-accent transition-colors">Scroll to explore</span>
            <ChevronDown className="w-5 h-5 text-ink animate-bounce" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
